import type { Editor } from '@tiptap/react';
import {
    Document as DocxDocument, Packer, Paragraph, TextRun, HeadingLevel, Table,
    TableRow, TableCell, WidthType, BorderStyle, AlignmentType, TableLayoutType,
    type IParagraphOptions,
} from 'docx';
import type { ProjectMember, Task } from '@/lib/schemas';
import { readTableGrid, fitTableWidths } from './document-table-grid';
import { materializeDocumentToc } from './document-toc';

export function sanitiseFilename(raw: string | null | undefined, fallback: string): string {
    return (raw ?? '').replace(/[\/:*?"<>|\x00-\x1F]/g, '').trim().replace(/\s+/g, '_') || fallback;
}
export function resolveMentionsInHtml(rawHtml: string, tasks: Task[], members: ProjectMember[]): string {
    const root = new DOMParser().parseFromString(rawHtml, 'text/html').body;
    for (const node of root.querySelectorAll('[data-type="mention"]')) {
        const id = node.getAttribute('data-id') || '';
        const kind = node.getAttribute('data-mention-kind') || node.getAttribute('data-kind');
        const label = node.getAttribute('data-label') || id;
        const replacement = document.createElement('a');
        replacement.textContent = kind === 'task' ? tasks.find(t => t.id === id)?.title || label
            : kind === 'person' ? members.find(m => m.user.id === id)?.user.name || label : label;
        if (kind === 'task' || kind === 'person') replacement.href = `/${kind === 'task' ? 'tasks' : 'members'}/${encodeURIComponent(id)}`;
        node.replaceWith(replacement);
    }
    return root.innerHTML;
}
export interface ExportDocumentOptions { fileName: string; tasks: Task[]; members: ProjectMember[]; language?: string }
function exportHtml(editor: Editor, options: ExportDocumentOptions) {
    // TOC is derived from this exact immutable document snapshot, not stale cached labels.
    return resolveMentionsInHtml(materializeDocumentToc(editor.getHTML(), options.language), options.tasks, options.members);
}

// Deliberately independent of Tailwind/preflight and live editor CSS. In particular,
// list paragraphs share the marker baseline and cells have no 4rem scrolling clamp.
const PDF_CSS = `
html,body { margin:0; padding:0; background:white; color:#18181b; }
* { box-sizing:border-box; }
.export { width:720px; padding:24px; font:12px/1.6 Arial,sans-serif; overflow-wrap:anywhere; }
p { margin:0 0 8px; } h1 { font-size:30px; line-height:1.2; } h2 { font-size:24px; line-height:1.3; } h3 { font-size:20px; line-height:1.4; }
h1,h2,h3 { margin:16px 0 8px; } ul,ol { margin:8px 0; padding-left:24px; }
li { line-height:1.6; } li > p:first-child { display:inline; margin:0; } li > p:not(:first-child) { margin:4px 0; }
table { border-collapse:collapse; table-layout:fixed; width:100% !important; min-width:0 !important; margin:8px 0; }
td,th { border:1px solid #888; padding:6px 8px; vertical-align:top; overflow-wrap:anywhere; }
td > *,th > * { max-height:none; overflow:visible; margin:0; } th { background:#f1f5f9; }
blockquote { border-left:3px solid #6366f1; padding-left:16px; margin:8px 0; }
pre { white-space:pre-wrap; } a { color:#4338ca; } img { max-width:100%; }
[data-document-toc] { border:1px solid #bbb; padding:12px; margin:12px 0; }
[data-document-toc] p { margin:0; }
`;
// R22 raster + encoding policy. html2canvas renders the WHOLE document into one
// canvas, so canvas height, peak memory and the embedded page images all grow
// with the document: a 40k-character document produced a 1440 x ~51,000 px
// canvas (~293 MB of RGBA) and 26 uncompressed 9.2 MB DeviceRGB page images
// (221 MB PDF). Scale and JPEG quality are the two levers that bound both.
const PDF_SCALE = 2;                 // device px per CSS px, normal documents
const PDF_SCALE_TALL = 1.5;          // very tall documents: 1/4 of the pixels, glyphs stay crisp
const TALL_DOCUMENT_PX = 6000;       // CSS px (~150 lines of prose) above which we downscale
const MAX_RASTER_HEIGHT_PX = 42000;  // device px; several engines refuse to allocate a taller canvas
                                     // 42,000 lets a document up to ~28,000 CSS px keep the full
                                     // 1.5 scale (the "US + Tâches" document is ~27,700 px tall).
                                     // Still well inside engine limits (65,535 px per dimension,
                                     // ~45 Mpx here vs the ~80 Mpx canvas the old code already
                                     // allocated for that document), and costs ~12 MB instead of
                                     // ~9 MB for it.
const MIN_RASTER_SCALE = 0.5;        // below this the raster would be an unreadable smear
const PDF_JPEG_QUALITY = 0.8;        // JPEG quality for every page slice
// R27.2 ink policy. The editor paints on a dark surface, the exported page is
// white, and a colour that is perfectly legible in one is invisible in the
// other. Content pasted from Google Docs arrives with inline colours — the
// "US + Taches" document stores `color: rgb(255,255,255)` on 33,830 of its
// 40,407 characters (plus near-white headings), which exported as white on
// white. Any colour that cannot be read on the page it is exported to is
// therefore remapped: whites and greys become the document ink colour, coloured
// text keeps its hue and is darkened just until it is legible.
const PAPER_INK = '#18181b';         // same ink PDF_CSS uses for body text
const PAPER_WHITE: [number, number, number] = [255, 255, 255];
const MIN_PAPER_CONTRAST = 3;        // below this the text is effectively invisible
const TARGET_PAPER_CONTRAST = 4.5;   // WCAG AA for body text, used when darkening
type Rgba = [number, number, number, number];
function parseCssColor(value: string): Rgba | null {
    const text = value.trim().toLowerCase();
    if (!text || text === 'transparent') return null;
    const fn = text.match(/^rgba?\(([^)]+)\)$/)?.[1];
    if (fn) {
        const parts = fn.split(/[\s,/]+/).filter(Boolean).map(Number);
        const [r, g, b, a] = parts;
        if (r !== undefined && g !== undefined && b !== undefined && [r, g, b].every(Number.isFinite)) {
            return [r, g, b, a !== undefined && Number.isFinite(a) ? a : 1];
        }
        return null;
    }
    const h = text.match(/^#([0-9a-f]{3,8})$/)?.[1];
    if (h) {
        const pair = (i: number) => parseInt(h.slice(i, i + 2), 16);
        if (h.length === 3 || h.length === 4) return [pair(0) * 17, pair(1) * 17, pair(2) * 17, 1];
        if (h.length === 6 || h.length === 8) return [pair(0), pair(2), pair(4), h.length === 8 ? pair(6) / 255 : 1];
    }
    if (text === 'white') return [255, 255, 255, 1];
    if (text === 'black') return [0, 0, 0, 1];
    return null;
}
function relativeLuminance(color: readonly number[]) {
    const channel = (value: number | undefined) => {
        const s = (value ?? 0) / 255;
        return s <= 0.03928 ? s / 12.92 : Math.pow((s + 0.055) / 1.055, 2.4);
    };
    return 0.2126 * channel(color[0]) + 0.7152 * channel(color[1]) + 0.0722 * channel(color[2]);
}
function contrastRatio(a: number, b: number) {
    const [hi, lo] = a > b ? [a, b] : [b, a];
    return (hi + 0.05) / (lo + 0.05);
}
function toHex([r, g, b]: [number, number, number]) {
    return '#' + [r, g, b].map(v => Math.max(0, Math.min(255, Math.round(v))).toString(16).padStart(2, '0')).join('');
}
function rgbToHsl([r, g, b]: [number, number, number]): [number, number, number] {
    const [rr, gg, bb] = [r / 255, g / 255, b / 255];
    const max = Math.max(rr, gg, bb), min = Math.min(rr, gg, bb), l = (max + min) / 2;
    if (max === min) return [0, 0, l];
    const d = max - min;
    const s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    const h = max === rr ? (gg - bb) / d + (gg < bb ? 6 : 0) : max === gg ? (bb - rr) / d + 2 : (rr - gg) / d + 4;
    return [h / 6, s, l];
}
function hslToRgb([h, s, l]: [number, number, number]): [number, number, number] {
    if (s === 0) return [l * 255, l * 255, l * 255];
    const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
    const p = 2 * l - q;
    const hue = (t: number) => {
        let tt = t;
        if (tt < 0) tt += 1;
        if (tt > 1) tt -= 1;
        if (tt < 1 / 6) return p + (q - p) * 6 * tt;
        if (tt < 1 / 2) return q;
        if (tt < 2 / 3) return p + (q - p) * (2 / 3 - tt) * 6;
        return p;
    };
    return [hue(h + 1 / 3) * 255, hue(h) * 255, hue(h - 1 / 3) * 255];
}
/**
 * Returns a replacement colour when `value` cannot be read on `background`,
 * otherwise null so the caller keeps the original untouched.
 */
function paperSafeColor(value: string, background: [number, number, number]): string | null {
    const rgba = parseCssColor(value);
    if (!rgba) return null;
    const backgroundLuminance = relativeLuminance(background);
    const own = relativeLuminance(rgba);
    if (contrastRatio(own, backgroundLuminance) >= MIN_PAPER_CONTRAST) return null;
    // A dark surface (code block, dark chip) needs light ink, not dark ink.
    if (backgroundLuminance < 0.4) return '#f4f4f5';
    const [h, s, l] = rgbToHsl([rgba[0], rgba[1], rgba[2]]);
    // Pasted white/grey text carries no colour information: give it normal ink.
    if (s < 0.12) return PAPER_INK;
    for (let lightness = Math.min(l, 0.45); lightness >= 0.12; lightness -= 0.03) {
        const candidate = hslToRgb([h, s, lightness]);
        if (contrastRatio(relativeLuminance(candidate), backgroundLuminance) >= TARGET_PAPER_CONTRAST) return toHex(candidate);
    }
    return PAPER_INK;
}
/** The colour actually painted behind `el`, compositing translucent layers over white. */
function effectiveBackground(el: HTMLElement, view: Window): [number, number, number] {
    const layers: Rgba[] = [];
    for (let node: HTMLElement | null = el; node; node = node.parentElement) {
        const layer = parseCssColor(view.getComputedStyle(node).backgroundColor);
        if (layer && layer[3] > 0.02) {
            layers.push(layer);
            if (layer[3] >= 0.999) break;
        }
    }
    let out: [number, number, number] = [...PAPER_WHITE];
    for (let i = layers.length - 1; i >= 0; i--) {
        const layer = layers[i];
        if (!layer) continue;
        const [r, g, b, a] = layer;
        out = [out[0] * (1 - a) + r * a, out[1] * (1 - a) + g * a, out[2] * (1 - a) + b * a];
    }
    return out;
}
/**
 * Rewrites every unreadable text colour inside an export tree, in place.
 * Only elements that actually fail the contrast test are touched, so authored
 * colours that already work on paper keep their exact value.
 */
function applyPaperSafeInk(root: HTMLElement, view: Window) {
    for (const el of Array.from(root.querySelectorAll<HTMLElement>('*'))) {
        const color = view.getComputedStyle(el).color;
        const rgba = parseCssColor(color);
        if (!rgba) continue;
        // Fast path: readable on white (the page background) needs no work.
        if (contrastRatio(relativeLuminance(rgba), relativeLuminance(PAPER_WHITE)) >= MIN_PAPER_CONTRAST) continue;
        const safe = paperSafeColor(color, effectiveBackground(el, view));
        if (safe) el.style.setProperty('color', safe, 'important');
    }
}
export async function exportDocumentAsPdf(editor: Editor, options: ExportDocumentOptions): Promise<Blob> {
    const html = exportHtml(editor, options);
    const [{ default: html2canvas }, { jsPDF }] = await Promise.all([import('html2canvas'), import('jspdf')]);
    // An off-screen iframe creates a separate viewport and stylesheet tree. Its
    // content is in normal flow at x=0 inside that viewport; nothing white is ever
    // painted in the application's viewport, even on a transparent page background.
    const frame = document.createElement('iframe');
    frame.setAttribute('data-document-export-frame', '');
    frame.setAttribute('aria-hidden', 'true');
    frame.tabIndex = -1;
    frame.style.cssText = 'position:fixed;left:-10000px;top:0;width:800px;height:600px;border:0;pointer-events:none';
    document.body.appendChild(frame);
    try {
        const isolated = frame.contentDocument!;
        isolated.open();
        isolated.write('<!doctype html><html><head></head><body><main class="export"></main></body></html>');
        isolated.close();
        const style = isolated.createElement('style');
        style.textContent = PDF_CSS;
        isolated.head.append(style);
        const wrapper = isolated.querySelector('main')!;
        wrapper.innerHTML = html;
        for (const table of wrapper.querySelectorAll('table')) {
            const { widths } = readTableGrid(table);
            table.querySelectorAll(':scope > colgroup').forEach(el => el.remove());
            const group = isolated.createElement('colgroup');
            const sum = widths.reduce((a,b) => a+b, 0);
            widths.forEach(w => { const col = isolated.createElement('col'); col.style.width = `${w / sum * 100}%`; group.append(col); });
            table.prepend(group);
        }
        await isolated.fonts.ready;
        // Pasted content can carry colours that only work on the dark editor
        // (see the R27.2 ink policy above): rewrite them before rasterising, so
        // the PDF shows the same text the editor shows.
        applyPaperSafeInk(wrapper, isolated.defaultView!);
        // html2canvas's manual text renderer uses font metrics different from the
        // browser's list-marker metrics. Native foreignObject painting keeps BOTH
        // on the same baseline (and uses the browser's table layout as well).
        const cssHeight = Math.max(1, wrapper.scrollHeight);
        // Pick the raster scale BEFORE rendering: the canvas is allocated at
        // CSS size x scale, so a tall document must be downscaled up front. The
        // ceiling keeps us inside the smallest canvas height engines still
        // allocate (32,767 px), so pathological documents lose sharpness instead
        // of failing outright.
        const rasterScale = Math.min(
            cssHeight > TALL_DOCUMENT_PX ? PDF_SCALE_TALL : PDF_SCALE,
            Math.max(MAX_RASTER_HEIGHT_PX / cssHeight, MIN_RASTER_SCALE)
        );
        const canvas = await html2canvas(wrapper, {
            scale: rasterScale, backgroundColor: '#ffffff', width: wrapper.offsetWidth,
            height: wrapper.scrollHeight, windowWidth: 800, windowHeight: wrapper.scrollHeight,
            scrollX: 0, scrollY: 0, logging: false, foreignObjectRendering: true,
        });
        const pdf = new jsPDF({ unit: 'mm', format: 'a4', orientation: 'portrait' });
        const width = pdf.internal.pageSize.getWidth();
        const height = pdf.internal.pageSize.getHeight();
        // Each page receives its own cropped image INSIDE physical margins.
        // Prefer a blank raster row near the boundary, rather than bisecting a
        // glyph. Tall continuous objects fall back to a bounded slice.
        const margin = 15;
        const mmPerPixel = (width - 2 * margin) / canvas.width;
        const capacity = Math.max(1, Math.floor((height - 2 * margin) / mmPerPixel));
        const context = canvas.getContext('2d')!;
        const pages: Array<{ start: number; end: number }> = [];
        for (let start = 0; start < canvas.height;) {
            let end = Math.min(canvas.height, start + capacity);
            if (end < canvas.height) {
                const searchStart = Math.max(start + Math.floor(capacity * 0.75), end - 160);
                if (end > searchStart) {
                    const strip = context.getImageData(0, searchStart, canvas.width, end - searchStart);
                    for (let y = end - searchStart - 1; y >= 0; y--) {
                        let blank = true;
                        for (let x = 0; x < canvas.width; x++) {
                            const i = (y * canvas.width + x) * 4;
                            if (Math.min(strip.data[i]!, strip.data[i + 1]!, strip.data[i + 2]!) < 245) { blank = false; break; }
                        }
                        if (blank) { end = searchStart + y + 1; break; }
                    }
                }
            }
            // A page is always at least one raster row tall: a zero-height canvas
            // makes addImage throw, and an empty slice would export a blank page.
            if (end <= start) end = Math.min(canvas.height, start + 1);
            pages.push({ start, end });
            const slice = document.createElement('canvas');
            slice.width = canvas.width; slice.height = end - start;
            slice.getContext('2d')!.drawImage(canvas, 0, start, canvas.width, end - start, 0, 0, slice.width, slice.height);
            if (start) pdf.addPage();
            // R22 fix: a PNG data URL is decoded by jsPDF to raw DeviceRGB and
            // embedded with NO filter at all (9,223,200 B for this A4 page), which
            // is what produced the 221 MB, unopenable export. JPEG is embedded
            // verbatim as a /DCTDecode stream, so neither the raw RGBA bytes nor a
            // re-encode pass is involved. The quality argument must be applied
            // here: handing jsPDF the canvas element would make it call
            // toDataURL('image/jpeg', 1.0) internally.
            pdf.addImage(slice.toDataURL('image/jpeg', PDF_JPEG_QUALITY), 'JPEG',
                margin, margin, width - 2 * margin, slice.height * mmPerPixel);
            slice.width = 0; slice.height = 0; // release the slice bitmap now, not at GC time
            start = end;
        }
        // DOM coordinates are measured inside the isolated viewport and mapped
        // through the SAME pixel slices as the page images. These are actual PDF
        // internal destination annotations, not merely painted HTML anchors.
        const origin = wrapper.getBoundingClientRect();
        const scale = canvas.width / wrapper.offsetWidth;
        const destinations = new Map<string, { pageNumber: number; top: number }>();
        for (const heading of wrapper.querySelectorAll<HTMLElement>('h1[id],h2[id],h3[id]')) {
            const y = (heading.getBoundingClientRect().top - origin.top) * scale;
            const index = pages.findIndex(p => y >= p.start && y < p.end);
            if (index >= 0) destinations.set(heading.id, { pageNumber: index + 1, top: margin + (y - pages[index]!.start) * mmPerPixel });
        }
        for (const anchor of wrapper.querySelectorAll<HTMLElement>('[data-toc-target]')) {
            const destination = destinations.get(anchor.dataset.tocTarget!);
            if (!destination) continue;
            for (const rect of Array.from(anchor.getClientRects())) {
                const top = (rect.top - origin.top) * scale;
                const bottom = (rect.bottom - origin.top) * scale;
                pages.forEach((slice, i) => {
                    const from = Math.max(top, slice.start), to = Math.min(bottom, slice.end);
                    if (to <= from) return;
                    pdf.setPage(i + 1);
                    pdf.link(margin + (rect.left - origin.left) * scale * mmPerPixel,
                        margin + (from - slice.start) * mmPerPixel, rect.width * scale * mmPerPixel,
                        (to - from) * mmPerPixel, destination);
                });
            }
        }
        // The full-document raster is dead weight from here on (hundreds of MB on
        // a long document); drop it before the blob is built. All coordinate
        // mapping above already captured `scale` and `mmPerPixel`.
        canvas.width = 0; canvas.height = 0;
        return pdf.output('blob');
    } finally { frame.remove(); }
}

const PAGE_WIDTH = 11906; // A4, DXA; margins 1 inch each
const PAGE_MARGIN = 1440;
const CONTENT_WIDTH = PAGE_WIDTH - PAGE_MARGIN * 2;
const BORDER = { style: BorderStyle.SINGLE, size: 4, color: '999999' } as const;
// R22: a literal newline inside a text node IS a line break in the editor, but a
// raw newline written into <w:t> renders as nothing in Word. Split the text and
// emit an explicit break run between the pieces so multi-line paragraphs and
// <pre>/code blocks keep their breaks. `docx` wraps string run text in
// xml:space="preserve", so leading/trailing spaces survive the split.
const TEXT_LINE_BREAK = /\r\n|\r|\n/;
function textRuns(text: string, inherited: Record<string, unknown>): TextRun[] {
    const out: TextRun[] = [];
    text.split(TEXT_LINE_BREAK).forEach((part, index) => {
        if (index) out.push(new TextRun({ ...inherited, break: 1 }));
        if (part) out.push(new TextRun({ ...inherited, text: part }));
    });
    return out;
}
function paragraph(el: Element, header = false): Paragraph {
    const runs: TextRun[] = [];
    const walk = (node: Node, inherited: Record<string, unknown>) => {
        if (node.nodeType === Node.TEXT_NODE) { runs.push(...textRuns(node.textContent || '', inherited)); return; }
        if (!(node instanceof Element)) return;
        const opts = { ...inherited };
        if (/^(STRONG|B)$/.test(node.tagName)) opts.bold = true;
        if (/^(EM|I)$/.test(node.tagName)) opts.italics = true;
        if (node.tagName === 'U') opts.underline = {};
        if (node.tagName === 'CODE') opts.font = 'Courier New';
        const style = (node as HTMLElement).style;
        if (style?.fontFamily) opts.font = style.fontFamily.split(',')[0]!.replace(/["']/g, '');
        if (style?.color) {
            // Word pages are always white here (no run shading is emitted), so a
            // colour that vanishes on paper is replaced by a legible one.
            const ink = paperSafeColor(style.color, PAPER_WHITE) ?? style.color;
            const rgb = ink.match(/\d+/g);
            if (rgb && rgb.length >= 3) opts.color = rgb.slice(0, 3).map(n => Number(n).toString(16).padStart(2, '0')).join('');
            else if (/^#[a-f\d]{6}$/i.test(ink)) opts.color = ink.slice(1);
        }
        if (style?.fontSize) opts.size = Math.round(parseFloat(style.fontSize) * (style.fontSize.endsWith('pt') ? 2 : 1.5));
        if (node.tagName === 'BR') { runs.push(new TextRun({ break: 1 })); return; }
        node.childNodes.forEach(child => walk(child, opts));
    };
    walk(el, { bold: header });
    const level = { H1: HeadingLevel.HEADING_1, H2: HeadingLevel.HEADING_2, H3: HeadingLevel.HEADING_3 }[el.tagName];
    const align = (el as HTMLElement).style.textAlign;
    const alignment = { center: AlignmentType.CENTER, right: AlignmentType.RIGHT, justify: AlignmentType.JUSTIFIED }[align];
    // R21: TOC entries carry data-toc-level (h1→1 … h3→3); indent them in the
    // exported document so the TOC reads as a hierarchy in Word too.
    const tocLevel = Number(el.getAttribute('data-toc-level') || 0);
    const indent = tocLevel > 1 ? tocLevel - 1 : 0;
    const opts: IParagraphOptions = { children: runs, heading: level, alignment, spacing: { after: 100 }, ...(indent > 0 ? { indent: { left: indent * 240 } } : {}) };
    return new Paragraph(opts);
}
function blocks(parent: Element, width = CONTENT_WIDTH, header = false): Array<Paragraph | Table> {
    const out: Array<Paragraph | Table> = [];
    for (const child of Array.from(parent.childNodes)) {
        if (child.nodeType === Node.TEXT_NODE) {
            // Same line-break handling as inline runs: block-level text nodes are
            // rare but must not lose their newlines either.
            if (child.textContent?.trim()) out.push(new Paragraph({ children: textRuns(child.textContent, {}) }));
            continue;
        }
        if (!(child instanceof Element)) continue;
        if (child.tagName === 'TABLE') { const table = htmlTableToDocx(child as HTMLTableElement, width); if (table) out.push(table); }
        else if (/^(P|H[1-6]|PRE)$/.test(child.tagName)) out.push(paragraph(child, header));
        else if (child.tagName === 'UL' || child.tagName === 'OL') {
            let n = Number(child.getAttribute('start') || 1);
            for (const li of Array.from(child.children)) {
                const copy = li.cloneNode(true) as Element;
                const p = copy.querySelector('p') || copy;
                p.prepend(document.createTextNode(child.tagName === 'OL' ? `${n++}. ` : '• '));
                out.push(...blocks(copy, width, header));
            }
        } else out.push(...blocks(child, width, header));
    }
    return out.length ? out : [new Paragraph('')];
}
export function htmlTableToDocx(table: HTMLTableElement, available = CONTENT_WIDTH): Table | null {
    const grid = readTableGrid(table);
    if (!grid.widths.length) return null;
    const columnWidths = fitTableWidths(grid.widths, available);
    const rows = grid.rows.map(row => new TableRow({
        tableHeader: row.length > 0 && row.every(c => c.element.tagName === 'TH'),
        children: row.map(cell => {
            const width = columnWidths.slice(cell.column, cell.column + cell.span).reduce((a,b) => a+b, 0);
            const children = blocks(cell.element, Math.max(1, width - 160), cell.element.tagName === 'TH');
            if (children[children.length - 1] instanceof Table) children.push(new Paragraph(''));
            return new TableCell({
                children, columnSpan: cell.span, rowSpan: cell.rowSpan,
                width: { size: width, type: WidthType.DXA },
                margins: { top: 80, bottom: 80, left: 80, right: 80 },
                shading: cell.element.tagName === 'TH' ? { fill: 'F1F5F9' } : undefined,
            });
        }),
    }));
    return new Table({ rows, columnWidths, layout: TableLayoutType.FIXED,
        width: { size: available, type: WidthType.DXA },
        borders: { top: BORDER, bottom: BORDER, left: BORDER, right: BORDER, insideHorizontal: BORDER, insideVertical: BORDER },
    });
}
export async function exportDocumentAsDocx(editor: Editor, options: ExportDocumentOptions): Promise<Blob> {
    const root = new DOMParser().parseFromString(exportHtml(editor, options), 'text/html').body;
    const doc = new DocxDocument({ creator: 'TaskFlow Document', title: options.fileName,
        styles: { default: { document: { run: { font: 'Arial', size: 18 } } } },
        sections: [{ properties: { page: { size: { width: PAGE_WIDTH, height: 16838 }, margin: { top: PAGE_MARGIN, bottom: PAGE_MARGIN, left: PAGE_MARGIN, right: PAGE_MARGIN } } }, children: blocks(root) }],
    });
    return Packer.toBlob(doc);
}
export function downloadBlob(blob: Blob, fileName: string, extension: string): void {
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = `${fileName}.${extension}`; a.style.display = 'none';
    document.body.appendChild(a); a.click();
    setTimeout(() => { a.remove(); URL.revokeObjectURL(url); }, 100);
}
